import React, { useState, useEffect } from 'react';
import { Bell, Clock, Loader2, TrendingUp, DollarSign, BarChart3, Send } from 'lucide-react';
import { api } from '../../utils/api';
import './WhatsAppPreferences.css';


function WhatsAppPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingTestAlert, setSendingTestAlert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const me = await api.getMe();
      setIsAdmin(!!me.is_admin);
      setPrefs({
        summary_enabled: me.report_frequency === 'biweekly' || me.report_frequency === 'monthly',
        send_hour: me.report_send_hour ?? 9,
        send_day: me.report_send_day ?? 0,
        alerts_enabled: me.alerts_enabled || false,
        alert_big_variances: me.alert_big_variances ?? true,
        alert_large_transactions: me.alert_large_transactions ?? true,
        alert_trend_changes: me.alert_trend_changes ?? true,
      });
    } catch (err) {
      setError('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreference = async (updates) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await api.updateReportPreferences(updates);
      setPrefs(prev => ({
        ...prev,
        summary_enabled: result.frequency === 'monthly' || result.frequency === 'biweekly',
        send_hour: result.send_hour,
        send_day: result.send_day,
        alerts_enabled: result.alerts_enabled,
        alert_big_variances: result.alert_big_variances,
        alert_large_transactions: result.alert_large_transactions,
        alert_trend_changes: result.alert_trend_changes,
      }));
      setMessage('Preferences saved!');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wa-prefs-loading">
        <Loader2 size={18} className="spinner" />
        <span>Loading preferences...</span>
      </div>
    );
  }

  if (!prefs) return null;

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hourOptions = [];
  for (let h = 0; h < 24; h++) {
    const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
    hourOptions.push({ value: h, label });
  }

  return (
    <div className="wa-prefs">
      <h3 className="wa-prefs-title">Notification Settings</h3>

      {error && <div className="wa-prefs-error">{error}</div>}
      {message && <div className="wa-prefs-success">{message}</div>}

      {/* Monthly Summary */}
      <div className="wa-prefs-section">
        <div className="wa-prefs-section-header">
          <Clock size={16} />
          <span>Monthly Summary</span>
        </div>
        <p className="wa-prefs-description">
          Get a financial recap on the 16th of each month covering the previous month — revenue, expenses, trends, top costs, and cash position.
        </p>

        <div className="wa-prefs-row">
          <label>Enable monthly summary</label>
          <label className="wa-toggle">
            <input
              type="checkbox"
              checked={prefs.summary_enabled}
              onChange={(e) => {
                const val = e.target.checked;
                setPrefs(prev => ({ ...prev, summary_enabled: val }));
                savePreference({
                  frequency: val ? 'monthly' : 'off',
                  timezone: userTimezone,
                });
              }}
              disabled={saving}
            />
            <span className="wa-toggle-slider"></span>
          </label>
        </div>

        {prefs.summary_enabled && (
          <>
            <p className="wa-prefs-schedule-note">Sent on the 16th of each month, covering the previous month's results.</p>

            <div className="wa-prefs-row">
              <label>Time</label>
              <select
                aria-label="Summary send time"
                value={prefs.send_hour}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setPrefs(prev => ({ ...prev, send_hour: val }));
                  savePreference({ send_hour: val, timezone: userTimezone });
                }}
                disabled={saving}
              >
                {hourOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div className="wa-prefs-row wa-prefs-test-row">
                <label>Send a test now</label>
                <button
                  className="wa-test-btn"
                  disabled={sendingTest || saving}
                  onClick={async () => {
                    setSendingTest(true);
                    setError('');
                    setMessage('');
                    try {
                      await api.testSummary();
                      setMessage('Test summary sent! Check your WhatsApp.');
                      setTimeout(() => setMessage(''), 4000);
                    } catch (err) {
                      setError(err.message || 'Failed to send test summary');
                    } finally {
                      setSendingTest(false);
                    }
                  }}
                >
                  {sendingTest ? (
                    <><Loader2 size={14} className="spinner" /> Sending...</>
                  ) : (
                    <><Send size={14} /> Send Test</>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Alerts */}
      <div className="wa-prefs-section">
        <div className="wa-prefs-section-header">
          <Bell size={16} />
          <span>Real-time Alerts</span>
        </div>

        <div className="wa-prefs-row">
          <label>Enable alerts</label>
          <label className="wa-toggle">
            <input
              type="checkbox"
              checked={prefs.alerts_enabled}
              onChange={(e) => {
                const val = e.target.checked;
                setPrefs(prev => ({ ...prev, alerts_enabled: val }));
                savePreference({ alerts_enabled: val });
              }}
              disabled={saving}
            />
            <span className="wa-toggle-slider"></span>
          </label>
        </div>

        {prefs.alerts_enabled && (
          <div className="wa-prefs-alerts-list">
            <div className="wa-prefs-alert-item">
              <div className="wa-prefs-alert-info">
                <BarChart3 size={14} />
                <span>Big variances month-over-month</span>
              </div>
              <label className="wa-toggle">
                <input
                  type="checkbox"
                  checked={prefs.alert_big_variances}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setPrefs(prev => ({ ...prev, alert_big_variances: val }));
                    savePreference({ alert_big_variances: val });
                  }}
                  disabled={saving}
                />
                <span className="wa-toggle-slider"></span>
              </label>
            </div>

            <div className="wa-prefs-alert-item">
              <div className="wa-prefs-alert-info">
                <DollarSign size={14} />
                <span>Large transactions</span>
              </div>
              <label className="wa-toggle">
                <input
                  type="checkbox"
                  checked={prefs.alert_large_transactions}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setPrefs(prev => ({ ...prev, alert_large_transactions: val }));
                    savePreference({ alert_large_transactions: val });
                  }}
                  disabled={saving}
                />
                <span className="wa-toggle-slider"></span>
              </label>
            </div>

            <div className="wa-prefs-alert-item">
              <div className="wa-prefs-alert-info">
                <TrendingUp size={14} />
                <span>Major trend changes</span>
              </div>
              <label className="wa-toggle">
                <input
                  type="checkbox"
                  checked={prefs.alert_trend_changes}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setPrefs(prev => ({ ...prev, alert_trend_changes: val }));
                    savePreference({ alert_trend_changes: val });
                  }}
                  disabled={saving}
                />
                <span className="wa-toggle-slider"></span>
              </label>
            </div>
          </div>
        )}

        {isAdmin && prefs.alerts_enabled && (
          <div className="wa-prefs-row wa-prefs-test-row">
            <label>Send a test alert</label>
            <button
              className="wa-test-btn"
              disabled={sendingTestAlert || saving}
              onClick={async () => {
                setSendingTestAlert(true);
                setError('');
                setMessage('');
                try {
                  await api.testAlert();
                  setMessage('Test alert sent! Check your WhatsApp.');
                  setTimeout(() => setMessage(''), 4000);
                } catch (err) {
                  setError(err.message || 'Failed to send test alert');
                } finally {
                  setSendingTestAlert(false);
                }
              }}
            >
              {sendingTestAlert ? (
                <><Loader2 size={14} className="spinner" /> Sending...</>
              ) : (
                <><Send size={14} /> Send Test</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhatsAppPreferences;
